import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Link,
    FormHelperText,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useUserSubscriptionRequest } from 'components/TrialBanner/queries/useSubscription';
import { useNotification } from 'context/NotificationContext';
import { useAuth } from 'context/AuthContext';
import styles from './styles.module.scss'

const validationSchema = Yup.object({
    plan: Yup.string().required('Plan is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    isYearly: Yup.boolean().required(),
});

const UpgradePlanModal = ({ open, onClose }) => {
    const { user } = useAuth();
    const showNotification = useNotification();

    const onSuccess = () => {
        showNotification(
            'Great choice! Our team will email you within one business day to arrange payment and activate your subscription.',
            'success',
            '',
            null
        );
        onClose();
    };

    const onError = () => {
        showNotification(
            'Error! Something went wrong, please try again later.',
            'error',
            '',
            null
        );
        onClose();
    };

    const mutation = useUserSubscriptionRequest({ onSuccess, onError });

    const isLoading = mutation.isLoading;

    const handleSubmit = async (values) => {
        await mutation.mutateAsync({
            subscription_type: values.plan === 'professional' ? 'Professional' : 'Enterprise',
            is_yearly: values.isYearly,
            email: values.email,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>
                Keep your tests on autopilot — pick your plan
            </DialogTitle>

            <Formik
                initialValues={{
                    plan: 'professional',
                    isYearly: false,
                    email: user?.email,
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                }) => (
                    <Form>
                        <DialogContent>
                            <FormControl
                                fullWidth
                                margin="dense"
                                error={touched.plan && Boolean(errors.plan)}
                            >
                                <InputLabel>Plan</InputLabel>
                                <Select
                                    name="plan"
                                    value={values.plan}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Plan"
                                >
                                    <MenuItem value="professional">
                                        Professional – $1079/mo ($899/mo billed yearly)
                                    </MenuItem>
                                    <MenuItem value="enterprise">
                                        Enterprise – Custom price
                                    </MenuItem>
                                </Select>
                                {touched.plan && errors.plan && (
                                    <FormHelperText>{errors.plan}</FormHelperText>
                                )}
                            </FormControl>

                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {values.plan === 'professional'
                                    ? '10 000 runs / mo, priority queue'
                                    : 'Private environment, custom limits, premium queue'}
                            </Typography>

                            <Link
                                href="/#price"
                                target="_blank"
                                sx={{ display: 'block', mt: 2 }}
                            >
                                Learn more about plans
                            </Link>

                            <FormControlLabel
                                sx={{mt: 2}}
                                control={
                                    <Switch
                                        checked={values.isYearly}
                                        onChange={(_, checked) =>
                                            setFieldValue('isYearly', checked)
                                        }
                                    />
                                }
                                label="Monthly / Yearly (save ~20 %)"
                            />

                            <TextField
                                sx={{mt: 2}}
                                fullWidth
                                label="Email"
                                name="email"
                                margin="dense"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </DialogContent>

                        <DialogActions sx={{px: 3, py: 2}}>
                            <Button
                                className={styles.button}
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || isLoading}
                            >
                                {isSubmitting || isLoading
                                    ? 'Submitting...'
                                    : 'Upgrade My Plan'}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default UpgradePlanModal;
